import { useState, useEffect, useRef } from "react";
import {
  cleanupWar,
  getFirstMessage,
  getWarMessage,
  resolveRound,
  revealLastCard,
  startNewGame,
} from "./WarGameHelpers";
import WarGameDisplay from "./WarGameDisplay";

export default function WarGame() {
  const intervalRef = useRef(null);
  const [isSimulating, setIsSimulating] = useState(false);
  const [stepPhase, setStepPhase] = useState("idle");
  const [previousStepPhase, setPreviousStepPhase] = useState(null);
  const [gameState, setGameState] = useState({
    kellyanDeck: [],
    kellyanEarnedCards: [],
    kellyanDownCards: [],
    kellyanCardVisiblity: [],
    garrettDeck: [],
    garrettEarnedCards: [],
    garrettDownCards: [],
    garrettCardVisiblity: [],
    message: "",
  });

  // Initialize game
  useEffect(() => {
    startNewGame(
      setGameState,
      setStepPhase,
      setIsSimulating,
      setPreviousStepPhase
    );
  }, []);

  useEffect(() => {
    // Handle cleanup if game over
    if (stepPhase === "game_over") {
      clearInterval(intervalRef.current);
      setIsSimulating(false);
      return;
    }

    if (!isSimulating && stepPhase !== "manual_step") return;

    const currentPhase =
      stepPhase === "manual_step" ? previousStepPhase : stepPhase;

    const timer = setTimeout(
      () => {
        advanceStep(currentPhase);
      },
      isSimulating ? 33 : 0
    );

    return () => clearTimeout(timer);
  }, [stepPhase, isSimulating, previousStepPhase]);

  // a function to advance the game state based on the current phase
  function advanceStep(currentPhase) {
    switch (currentPhase) {
      case "flip_kellyan":
        setGameState((prev) => ({ ...prev, kellyanCardVisiblity: [true] }));
        setStepPhase("flip_garrett");
        break;
      case "flip_garrett":
        setGameState((prev) => ({
          ...prev,
          garrettCardVisiblity: [true],
          message: getFirstMessage(prev),
        }));
        setStepPhase("resolve");
        break;
      case "resolve":
        resolveRound(setGameState, setStepPhase);
        break;
      case "war_flip_k":
        setGameState((prev) => ({
          ...prev,
          kellyanCardVisiblity: revealLastCard(prev.kellyanCardVisiblity),
        }));
        setStepPhase("war_flip_g");
        break;
      case "war_flip_g":
        setGameState((prev) => ({
          ...prev,
          garrettCardVisiblity: revealLastCard(prev.garrettCardVisiblity),
          message: getWarMessage(prev),
        }));
        setStepPhase("war_reveal_all");
        break;
      case "war_reveal_all":
        setGameState((prev) => ({
          ...prev,
          kellyanCardVisiblity: prev.kellyanCardVisiblity.map(() => true),
          garrettCardVisiblity: prev.garrettCardVisiblity.map(() => true),
        }));
        setStepPhase("war_cleanup");
        break;
      case "war_cleanup":
        cleanupWar(setGameState, setStepPhase);
        break;
      default:
        break;
    }
  }

  // Triggered by simulate button
  function handleSimulateGameClick() {
    setIsSimulating(!isSimulating);
  }

  // Triggered by action button
  function handleActionButtonClick() {
    setPreviousStepPhase(stepPhase); // remember what the user wants to do
    setStepPhase("manual_step");
  }

  // Triggered by new game button
  function handleNewGameClick() {
    startNewGame(
      setGameState,
      setStepPhase,
      setIsSimulating,
      setPreviousStepPhase
    );
  }

  return (
    <WarGameDisplay
      gameState={gameState}
      stepPhase={stepPhase}
      isSimulating={isSimulating}
      handleNewGameClick={handleNewGameClick}
      handleActionButtonClick={handleActionButtonClick}
      handleSimulateGameClick={handleSimulateGameClick}
    />
  );
}
